import type { ActionCard, AiPlayContextParams } from '@playbooks/core/src/interfaces'
import type { ActionCardsSettings } from '../../types'

/**
 * Build the template identity sent with AI widget requests.
 *
 * An actual play is authoritative when one exists, and its source must travel with it:
 * local and tenant play IDs are separate sequences that would otherwise be
 * indistinguishable to the backend. Editor and web-play previews have no play yet and
 * fall back to the template the Action Card belongs to.
 */
export function getAiPlayContext(settings: ActionCardsSettings, actionCardsData: ActionCard): AiPlayContextParams {
  const playId = settings.playId ?? undefined

  // If available, provide the play ID and its source
  if (playId !== undefined) {
    return { playId, playSource: settings.playSource ?? 'tenant' }
  }

  // No play available (we are in playbook/template scope). Provide template ID instead.
  // Use the template ID provided by the host app; `actionCardsData.templateId`
  // is a only a fallback, can be empty in legacy Action Cards.
  return { templateId: toTemplateId(settings.templateId ?? actionCardsData.templateId) }
}

/**
 * Keep only a usable template ID.
 *
 * legacy Action Cards default their `templateId` to `'0'` when it was never stored;
 * it's not a valid ID and should be ignored
 */
function toTemplateId(value: number | string | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined

  const normalized = String(value).trim()
  return /^[1-9]\d*$/.test(normalized) ? normalized : undefined
}
